import { Injectable } from '@angular/core';
import { Observable, catchError, of, switchMap, take, throwError, tap } from 'rxjs';
import { AppSettingsService } from 'src/app/services/app-settings.service';
import { HttpService } from 'src/app/services/http.service';
import { BaseResponseDto } from 'src/app/shared/models/BaseResponseDto.model';
import { SearchOptionsResponse } from 'src/app/shared/models/SearchOptionsResponse.model';
import { AbstractRestService } from 'src/app/shared/types/AbstractRestService';
import { AnalyticsServiceNameModel, TelemetryService } from 'telemetry-library';
import { TransactionNoteDto } from './models/transactionNoteDto.model';
import { TransactionsDto } from './models/transactionsDto.model';
import { TransactionsSearchOptionsDto } from './models/transactionsSearchOptionsDto.model';
import { UserService } from 'src/app/services/shared/user.service';

export type TransactionExportFileType = 'csv' | 'excel';
@Injectable({
  providedIn: 'root'
})
export class TransactionsMsService extends AbstractRestService<TransactionsDto | BaseResponseDto, TransactionsDto | BaseResponseDto>{
  protected readonly baseEndpoint$ = this.appSettingsService.localEnv ?
    of('https://localhost:44360/api/transactions/') :
    of('transactions/');

  constructor(
    protected http: HttpService,
    private appSettingsService: AppSettingsService,
    private userService: UserService,
    private telemetryService: TelemetryService
  ) {
    super(http);
  }

  public getAll(clientBankAccountId: number): Observable<TransactionsDto[] | BaseResponseDto> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.getTyped<TransactionsDto[] | BaseResponseDto>(`${endpoint}list/${clientBankAccountId}`)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public getTransactions(bankAccountId: number, searchOption: TransactionsSearchOptionsDto, endPointType: 'unit' | 'external'): Observable<TransactionsDto[] | SearchOptionsResponse<TransactionsDto> | BaseResponseDto> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.getTyped<SearchOptionsResponse<TransactionsDto> | BaseResponseDto>(`${endpoint}${endPointType}/${bankAccountId}${this.getTransactionsSearchOptionsToQuerystring(searchOption)}`)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public exportTransactions(fileType: TransactionExportFileType, unitBankId: number, options: TransactionsSearchOptionsDto, endPointType: 'unit' | 'external'): Observable<any> {
    const queryParams = [
      (options ? this.getTransactionsSearchOptionsToQuerystring(options) : ''),
      `fileType=${fileType}`
    ];

    let queryParamsStr = queryParams.join('&');
    if (!queryParamsStr.startsWith('?')) {
      queryParamsStr = '?' + queryParamsStr;
    }
    const contentType = fileType === 'excel' ? 'application/vnd.ms-excel' : 'text/csv';

    this.sendTelemetryEvent(fileType);

    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.downloadFile(`${baseEndpoint}${endPointType}/${unitBankId}/transactions/export${(queryParams.length ? `${queryParamsStr}` : '')}`, contentType)),
      catchError(err => {
        return throwError(err);
      }
      )
    );
  }

  createTransactionNotes(transactionNotes: TransactionNoteDto): Observable<TransactionNoteDto> {
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl =>
        this.http.postTyped<TransactionNoteDto>(`${baseUrl}note/create`, transactionNotes)
      )
    );
  }

  updateTransactionNotes(transactionNotes: TransactionNoteDto): Observable<TransactionNoteDto> {
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl =>
        this.http.putTyped<TransactionNoteDto>(`${baseUrl}note/update`, transactionNotes)
      )
    );
  }

  public deleteTransactionNotes(transactionNoteId: number): Observable<void> {
    return this.baseEndpoint$.pipe(
      switchMap(baseUrl => this.http.deleteTyped<void>(
        `${baseUrl}note/${transactionNoteId}`)));
  }

  private getTransactionsSearchOptionsToQuerystring(options: TransactionsSearchOptionsDto): string {
    const baseSearchOptionsQueryString = super.getSearchOptionsToQuerystring(options);

    const startDate = options.startDate?.toISOString() || '';
    const endDate = options.endDate?.toISOString() || '';
    const typeName = options.typeName || '';
    const excludeFees = options.excludeFees || '';

    return baseSearchOptionsQueryString + `&startDate=${startDate}&endDate=${endDate}&typeName=${typeName}&excludeFees=${excludeFees}`;
  }

  private sendTelemetryEvent(fileType: TransactionExportFileType): void {
    this.userService.accountDetails$
      .pipe(take(1)).subscribe(userDetails => {
      this.telemetryService.create({
        eventID: '3004',
        eventTitle: 'EXPORT BANK TRANSACTIONS - CLICKED',
        fileType: fileType.toUpperCase(),
        ctNumber: userDetails.clientIdentifier
      }, AnalyticsServiceNameModel.Mixpanel);
    });
  }

}
